import { useCallback, useEffect } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";

import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { toast } from "@/utils/toastService";

import { useDeleteAssetAttachment } from "../asset-attachments.query";
import type { AssetAttachment } from "../asset-attachments.types";

interface DeleteAssetAttachmentModalProps {
  show: boolean;
  onClose: () => void;
  assetUuid: string;
  attachment: AssetAttachment;
}

// Mirrors DeleteValidationModal. A failed delete toasts and leaves the modal
// open for a retry; the hook treats a 404 as a success. Escape and the close
// button dismiss it, as in DeleteCaseFileModal, except while a delete runs.
export const DeleteAssetAttachmentModal: React.FC<DeleteAssetAttachmentModalProps> =
  ({ show, onClose, assetUuid, attachment }) => {
    const { mutate, isPending } = useDeleteAssetAttachment(assetUuid);

    const handleSubmit = () =>
      mutate(attachment.uuid, {
        onSuccess: onClose,
        onError: (error) => toast.error(error.message),
      });

    const handleClose = useCallback(() => {
      if (isPending) return;
      onClose();
    }, [isPending, onClose]);

    // The base Modal does not dismiss on escape.
    useEffect(() => {
      if (!show) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") handleClose();
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [show, handleClose]);

    return (
      <Modal open={show} onClose={handleClose} showCloseButton={true}>
        <Modal.Title>Delete Attachment</Modal.Title>
        <div style={{ fontWeight: 600, margin: "20px 0px" }}>
          Are you sure you want to delete this attachment: "
          {attachment.name ?? attachment.uuid}"?
        </div>
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="error"
            variant="contained"
            onClick={handleSubmit}
            disabled={isPending}
            loading={isPending}
          >
            Delete Attachment
          </Button>
        </ButtonMenu>
      </Modal>
    );
  };
